import { Injectable, EventEmitter, NgZone } from '@angular/core';
import { Platform } from '@ionic/angular';
import dayjs from 'dayjs';
import { UserStore } from '../store/user.store';
import { PrenotazioniStore } from '../store/prenotazioni.store';
import { RichiesteSedeStore } from '../store/richieste-sede.store';
import { SediStore } from '../store/sedi.store';
import { SocketService } from './socket.service';
import { NetworkService } from './network.service';

/** Soglia minima di permanenza in background (60s) per far scattare la risincronizzazione automatica al resume */
const SYNC_MIN_AWAY_MS = 60_000;

@Injectable({
  providedIn: 'root'
})
export class DataSyncService {
  /** Notifica ai componenti attivi (es. HomePage, MiePrenotazioniPage) che una sincronizzazione è avvenuta */
  public syncCompleted: EventEmitter<string> = new EventEmitter<string>();

  private lastSyncTime: number = Date.now();
  private lastHiddenAt: number | null = null;
  private lastDayStr: string = dayjs().format('YYYY-MM-DD');
  private isSyncing: boolean = false;
  private initialized: boolean = false;

  constructor(
    private platform: Platform,
    private zone: NgZone,
    private userStore: UserStore,
    private prenotazioniStore: PrenotazioniStore,
    private richiesteSedeStore: RichiesteSedeStore,
    private sediStore: SediStore,
    private socketService: SocketService,
    private networkService: NetworkService
  ) {}

  public init(): void {
    if (this.initialized) return;
    this.initialized = true;
    console.log('[DataSyncService] Inizializzato monitoraggio sincronizzazione e resume');

    // 1. Visibilità pagina / Cambio scheda / Sblocco schermo mobile
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.lastHiddenAt = Date.now();
        return;
      }
      const awayMs = this.lastHiddenAt ? Date.now() - this.lastHiddenAt : 0;
      this.lastHiddenAt = null;

      const todayStr = dayjs().format('YYYY-MM-DD');
      const dayChanged = todayStr !== this.lastDayStr;

      if (awayMs >= SYNC_MIN_AWAY_MS || dayChanged) {
        this.zone.run(() => {
          void this.syncAll(dayChanged ? 'day-changed' : 'resume');
        });
      }
    });

    // 2. Focus finestra del browser
    window.addEventListener('focus', () => {
      const elapsed = Date.now() - this.lastSyncTime;
      if (elapsed >= SYNC_MIN_AWAY_MS) {
        this.zone.run(() => {
          void this.syncAll('focus');
        });
      }
    });

    // 3. Ritorno della connessione online
    window.addEventListener('online', () => {
      this.zone.run(() => {
        void this.syncAll('network-online');
      });
    });

    // 4. Resume nativo Ionic (Cordova / Capacitor / PWA)
    this.platform.resume.subscribe(() => {
      const elapsed = Date.now() - this.lastSyncTime;
      if (elapsed >= SYNC_MIN_AWAY_MS) {
        this.zone.run(() => {
          void this.syncAll('platform-resume');
        });
      }
    });

    // 5. Riconnessione Socket.IO
    this.socketService.updateStoreReconnected.subscribe(() => {
      this.zone.run(() => {
        void this.syncAll('socket-reconnected');
      });
    });
  }

  /**
   * Sincronizza tutti gli store principali se l'utente è loggato e online.
   */
  public async syncAll(reason: string, force: boolean = false): Promise<void> {
    if (!this.userStore.userLogged) {
      return;
    }
    if (!this.networkService.isOnline && !force) {
      console.log(`[DataSyncService] Skip sync (${reason}): offline`);
      return;
    }
    if (this.isSyncing) {
      console.log(`[DataSyncService] Sync già in corso, richiesta ignorata (${reason})`);
      return;
    }

    this.isSyncing = true;
    console.log(`[DataSyncService] Avvio sincronizzazione dati (motivo: ${reason})...`);

    try {
      // Verifica e ripristina la connessione socket se caduta
      this.socketService.ensureConnected();

      const syncTasks: Promise<any>[] = [
        this.prenotazioniStore.initStore(),
        this.richiesteSedeStore.refresh()
      ];

      const todayStr = dayjs().format('YYYY-MM-DD');
      if (todayStr !== this.lastDayStr || force) {
        syncTasks.push(this.sediStore.reload().catch(err => {
          console.warn('[DataSyncService] Errore ricaricamento sedi', err);
        }));
      }

      await Promise.all(syncTasks);

      this.lastSyncTime = Date.now();
      this.lastDayStr = todayStr;
      console.log(`[DataSyncService] Sincronizzazione completata con successo (${reason})`);
      this.syncCompleted.emit(reason);
    } catch (err) {
      console.error(`[DataSyncService] Errore durante sincronizzazione (${reason})`, err);
    } finally {
      this.isSyncing = false;
    }
  }
}
